import { useMemo } from 'react';

import { useRepositories } from '@spotify/confidence-core-react';
import { useFeatureFlag } from '@spotify/confidence-flag-evaluation-react';
import { type RepositoriesV1Repository } from '@spotify/confidence-graphql';

import { type CodingSessionApplication } from './buildCodingSessionLaunchUrl';
import { getRepositoryLabel } from './getRepositoryLabel';

const XIRP_ISSUE_LAUNCH_FLAG = 'xirp-issue-launch';
const XIRP_FOR_SPOTIFY_SESSION_LAUNCH_FLAG = 'chirp-session-launch';

type UseIssueLaunchRepositoriesResult = ReturnType<typeof useRepositories> & {
  areRepositoryLaunchActionsAvailable: boolean;
  enabledApplications: CodingSessionApplication[];
  repositoryOptions: RepositoryOption[];
};

export type RepositoryOption = {
  label: string;
  repository: Pick<RepositoriesV1Repository, 'host' | 'htmlUrl' | 'name'>;
  value: string;
};

export const useXirpForSpotifySessionLaunchEnabled = (): boolean => {
  const { enabled } = useFeatureFlag<{ enabled: boolean }>(
    XIRP_FOR_SPOTIFY_SESSION_LAUNCH_FLAG,
    { enabled: false },
  );

  return enabled;
};

const getEnabledApplications = ({
  isXirpForSpotifySessionLaunchEnabled,
  isXirpIssueLaunchEnabled,
}: {
  isXirpForSpotifySessionLaunchEnabled: boolean;
  isXirpIssueLaunchEnabled: boolean;
}): CodingSessionApplication[] => {
  if (isXirpForSpotifySessionLaunchEnabled) {
    return ['Xirp for Spotify'];
  }
  if (isXirpIssueLaunchEnabled) {
    return ['Xirp'];
  }
  return [];
};

export const useIssueLaunchRepositories =
  (): UseIssueLaunchRepositoriesResult => {
    const isXirpForSpotifySessionLaunchEnabled =
      useXirpForSpotifySessionLaunchEnabled();
    const { enabled: isXirpIssueLaunchEnabled } = useFeatureFlag<{
      enabled: boolean;
    }>(XIRP_ISSUE_LAUNCH_FLAG, { enabled: false });
    const repositoriesResult = useRepositories({
      fetchPolicy: 'cache-first',
      skip: !isXirpIssueLaunchEnabled && !isXirpForSpotifySessionLaunchEnabled,
    });
    const repositoryOptions = useMemo(
      () =>
        (repositoriesResult.repositories ?? []).map(repository => ({
          label: getRepositoryLabel(repository.name),
          repository,
          value: repository.name,
        })),
      [repositoriesResult.repositories],
    );
    const enabledApplications = getEnabledApplications({
      isXirpForSpotifySessionLaunchEnabled,
      isXirpIssueLaunchEnabled,
    });

    return {
      ...repositoriesResult,
      areRepositoryLaunchActionsAvailable:
        !repositoriesResult.isLoading &&
        (Boolean(repositoriesResult.error) || repositoryOptions.length > 0),
      enabledApplications,
      repositoryOptions,
    };
  };
